import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// Send the session cookie on every request so cookie-based sessions work even
// when this tab has no token in sessionStorage (backend checks cookie first).
axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(sessionStorage.getItem('bookr_token'));

  const axiosAuth = useCallback(() => {
    const instance = axios.create({ baseURL: API, withCredentials: true });
    if (token) {
      instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    return instance;
  }, [token]);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUser(res.data);
    } catch (err) {
      console.error('Auth check failed:', err?.response?.status);
      setUser(null);
      sessionStorage.removeItem('bookr_token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    // Try cookie-based auth first (works even without token in storage)
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    const { token: newToken, user: userData } = res.data;
    sessionStorage.setItem('bookr_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, role = 'customer') => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password, role }, { withCredentials: true });
    const { token: newToken, user: userData } = res.data;
    sessionStorage.setItem('bookr_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  // Deprecated: the legacy Emergent-hosted OAuth redirect. We now use our own
  // Google Identity Services flow (rendered in AuthPage only when
  // GOOGLE_CLIENT_ID is configured). Kept as a no-op so any stale caller can't
  // send users off to a third-party service we no longer use.
  const loginWithGoogle = () => {
    console.warn('loginWithGoogle is deprecated; configure GOOGLE_CLIENT_ID to enable Google sign-in.');
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) { console.error('Logout error:', err); }
    sessionStorage.removeItem('bookr_token');
    // Also clear any legacy localStorage tokens
    localStorage.removeItem('bookr_token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, token, setToken, checkAuth, login, register, loginWithGoogle, logout, axiosAuth, API }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
